import React, { ComponentProps, useState } from 'react';
import { useTranslation } from 'react-i18next';
import i18n from 'i18next';
import { Namespace } from '../../../translation/namespace';
import { useBlockContext, useRefreshBlock } from '../../../_lib/BlockFetcher';
import { getTranslationNamespaceByElementType } from '../PersonExtendedDataBlock.utils';
import { asFetchableView } from '../../../_lib/ViewFetcher';
import { Form, Input, Row, SonForm, SubmitButton, Switch, Textarea, useForm } from '../../../_lib/Form2';
import { useHtmlValidId } from '../../../hooks/useHtmlValidId';
import { HiddenInput } from '../../../_lib/Form2/elements/HiddenInput/HiddenInput';
import { ButtonAction } from '../../../components/molecules/Btn/ButtonWrapper';
import ModalDialog from '../../../components/organisms/ModalDialog/ModalDialog';
import { EmailVerificationForm, EmailVerificationFormTypes } from '../partials/EmailVerificationForm';
import { SwitchModifier } from '../../../_lib/Form2/elements/Switch/Switch';

type EditableContentForm = {
  form: SonForm<any>;
  emailVerificationForm: SonForm<EmailVerificationFormTypes>;
};

export function EditableContentForm({ form, emailVerificationForm }: EditableContentForm) {
  const { elementType } = useBlockContext();
  const translationNamespace = getTranslationNamespaceByElementType(elementType);
  const { t } = useTranslation(translationNamespace);

  const [showChangeEmailModal, setShowChangeEmailModal] = useState(false);

  const { delayedRefresh } = useRefreshBlock();
  const id = useHtmlValidId();

  const { formProps, fetchState } = useForm({ form, onSuccess: delayedRefresh });

  let additionalEmailAddressCounter = 0;

  const getHelpText = (param: any): string | undefined => {
    switch (true) {
      case param.readProtected === true:
        return t(`${Namespace.phrases}:readProtected.personalData`);
      case param.name.includes('additionalEmail'):
        return t(`${Namespace.phrases}:labelHelpText.email`);
      case i18n.exists(`${Namespace.phrases}:labelHelpText.${param.name}`):
        return t(`${Namespace.phrases}:labelHelpText.${param.name}`);
      default:
        return undefined;
    }
  };

  /**
   * dynamically mapped parameters to allow BE to specify order.
   */
  const mappedParameters = form.params
    .map(param => {
      switch (true) {
        case param.name === 'institution':
          return (
            <Input label={t(param.name)} param={param} helpText={getHelpText(param)} autoComplete="organization" />
          );
        case param.type === 'tel':
          return <Input label={t(param.name)} param={param} helpText={getHelpText(param)} autoComplete="tel" />;
        case param.type === 'email' && param.name.includes('additionalEmail'):
          additionalEmailAddressCounter = additionalEmailAddressCounter + 1;
          return (
            <Input
              label={t('additionalEmailAddress', { number: additionalEmailAddressCounter })}
              param={param}
              helpText={getHelpText(param)}
              autoComplete="email"
            />
          );
        case param.type === 'email':
          return (
            <div className="person-extended-data__email">
              <Input label={t(param.name)} param={param} helpText={getHelpText(param)} autoComplete="email" />
              {emailVerificationForm ? (
                <div className="person-extended-data__email-request-button">
                  <ButtonAction
                    type="button"
                    text={t('PersonExtendedData:changeEmail.headline')}
                    onClick={() => setShowChangeEmailModal(true)}
                  />
                </div>
              ) : null}
            </div>
          );
        case param.type === 'text':
        case param.type === 'number':
        case param.type === 'url':
        case param.type === 'password':
          return <Input label={t(param.name)} param={param} helpText={getHelpText(param)} />;
        case param.type === 'textarea':
          return <Textarea label={t(param.name)} param={param} helpText={getHelpText(param)} />;
        case param.type === 'checkbox':
          return <Switch modifierLabel={SwitchModifier.POSITION_INHERIT} label={t(param.name)} param={param} />;
        case param.type === 'hidden':
          return <HiddenInput {...param} />;
        default:
          return null;
      }
    })
    .map((component, index) => {
      if (!component) return null;
      const isHidden = form.params[index]?.type === 'hidden';
      return isHidden ? component : <Row key={index}>{component}</Row>; // don't return hidden inputs in <Row>!
    });

  return (
    <div className="person-extended-data__form">
      <Form {...formProps} id={`user-edit-form-${id}`}>
        {mappedParameters}
        <SubmitButton
          buttonLabel={t('submitLabel')}
          messageSuccess={t(`${Namespace.Form}:status.success`)}
          messageSending={t(`${Namespace.Form}:status.sending`)}
          messageFail={t(`${Namespace.Form}:status.fail`)}
          fetchState={fetchState}
        />
      </Form>
      {showChangeEmailModal && (
        <ModalDialog
          closeModal={() => setShowChangeEmailModal(false)}
          headline={t('PersonExtendedData:changeEmail.headline')}
          initialFocusIndex={1}
        >
          <EmailVerificationForm form={emailVerificationForm} />
        </ModalDialog>
      )}
    </div>
  );
}

export const PersonExtendedDataListEditView = asFetchableView(EditableContentForm);
export type PersonExtendedDataListEditView = ComponentProps<typeof EditableContentForm>;
